import * as THREE from 'three';
import { cameraControls, getActiveCamera } from './Initial3DSetup';
import { clampToGate, walkDelta } from '../../Utils/lobbyWalk';
import { applyLookDelta } from '../../Utils/lobbyLook';
import { raycastFromCentre } from '../../Utils/GlobalFunctions';
import { getSnapshot } from '../../Utils/loadProgress';

// The gated lobby's camera: WASD walking, and free look driven by the pointer. The
// listeners and the per-frame step live here; the geometry of both is in
// ../../Utils/lobbyWalk.js and ../../Utils/lobbyLook.js, out of three.js's way so it
// can be tested under jsdom.
//
// On a machine with a mouse the look holds the pointer lock — see `deviceLocksPointer`.
// The lobby has no visible cursor while that is held. The reticle at the centre of the
// frame (App.js, styled in App.css) is the aim point, and the click that opens the doors
// is a ray down the middle of the frame rather than at a pointer.

/** The keys the walk steers with, by event.code — 'KeyW' and friends. */
const STEERING_KEYS = /^key[wasd]$/i;

/**
 * Longest frame the walk will believe in. A backgrounded tab stops being drawn and
 * comes back with seconds of clock in hand; without this the camera would lurch the
 * whole length of the lobby the moment the tab was looked at again. The look needs no
 * such care — it is driven by how far the mouse moved, not by how long anything took.
 */
const MAX_FRAME_SECONDS = 0.1;

/**
 * OrbitControls is given its look-at target a tenth of a unit in front of the camera
 * (LobbyContainer.js), which is what makes a drag rotate the camera in place rather
 * than swing it around a point behind it. The walk and the look both keep that true.
 */
const TARGET_OFFSET = 0.1;

/** The button that opens the doors, by the name it carries inside the gate's GLB. */
const BUTTON_NAME = 'DoorButton';

const held = new Set();
const clock = new THREE.Clock();
const facing = new THREE.Vector3();
const euler = new THREE.Euler(0, 0, 0, 'YXZ');

/**
 * Where the camera is looking — yaw and pitch, carried from frame to frame. Only a mouse
 * movement ever changes it, which is what guarantees a resting hand cannot turn the
 * camera; see ../../Utils/lobbyLook.js.
 */
let look = { yaw: 0, pitch: 0 };

let gate = null;
let button = null;
let canvas = null;
let reticle = null;
let listening = false;

/**
 * Whether the camera has been aimed yet. Until it has, the camera is left where the
 * fly-by parked it, so its arrival is not snapped to straight ahead by a look nobody has
 * asked for.
 */
let looked = false;

/** Whether this device's look drives the pointer lock. See `deviceLocksPointer`. */
let locksPointer = false;

/** Last point of a touch drag, which is re-anchored as the drag goes on. */
let lastTouch = null;

const steers = (event) => STEERING_KEYS.test(event.code);

function onKeyDown(event) {
    if (!steers(event)) return;

    // Ctrl+W closes the tab, and no page gets to argue. Leave every modified key to the
    // browser and steer only on the plain ones.
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    // The keys are dead until the scene is up, for the same reason the look is: the lobby is
    // behind a loading screen, and walking it out of sight only leaves the camera somewhere
    // nobody asked for.
    if (!getSnapshot().finished) return;

    held.add(event.code);
    event.preventDefault();
}

function onKeyUp(event) {
    if (steers(event)) held.delete(event.code);
}

/**
 * Switching away from the tab mid-stride never delivers the keyup, which would leave the
 * camera walking off on its own the moment it came back into view.
 */
function onBlur() {
    held.clear();
}

/**
 * Whether the look should hold the pointer lock on this device. A mouse or trackpad
 * does: the lock is what hides the cursor, keeps movements coming once the pointer has
 * reached the edge of the window instead of stopping dead there, and — because a locked
 * mouse reports no movement at all while it is still — is the most direct guarantee that
 * a resting hand never turns the camera.
 *
 * A touch device gets none of it: there is nothing to lock, and a finger's drag reports
 * the same movements anyway, so the lock would buy it nothing and cost it the gesture.
 */
function deviceLocksPointer() {
    return typeof document.body.requestPointerLock === 'function'
        && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/**
 * Asks for the lock on a click in the lobby. The click keeps its own job — MainScene's
 * handler fires the door ray — so one click both engages the look and, if the reticle is
 * on the button, opens the doors. Nothing here stops the other handler running.
 */
function onCanvasClick() {
    if (!gate || !locksPointer || document.pointerLockElement) return;

    // Not until the scene is ready. The loading overlay lets pointer events through, so the
    // click that was meant for the loader would take the lock behind it and leave a
    // cursor-less pointer on a scene that is still being built. MainScene asks the same
    // question of the same flag, so the two halves of one click start together or not at all.
    if (!getSnapshot().finished) return;

    if (canvas) canvas.requestPointerLock();
}

/**
 * The cursor is hidden only while the lock is actually held: Esc hands the mouse back,
 * and the cursor returning with it is how the player sees that they have it again.
 */
function onPointerLockChange() {
    if (!canvas) return;

    canvas.classList.toggle('lobby-look', document.pointerLockElement === canvas);
}

/**
 * A refused lock is not fatal — the drag path in `onMouseMove` is the fallback, and it
 * exists for exactly this. Worth a word, though, since the cursor staying visible and
 * nothing happening is otherwise a silent failure.
 */
function onPointerLockError() {
    console.warn('Lobby: the pointer lock was refused — hold the left button and drag to look.');
}

/** Applies a movement to the look, and remembers that the camera has now been aimed. */
function moveLook(dx, dy) {
    // Nothing turns the camera while the scene is still loading. A drag reaches this the way a
    // click reaches the door ray — the overlay is pointer-transparent — and the movement is
    // dropped rather than queued, including `looked`: recording it would leave the look where
    // the drag had taken it and snap the camera to that orientation on the loader's last frame.
    // The camera stays on the pose the fly-by parked it on until the scene is up.
    if (!getSnapshot().finished) return;

    look = applyLookDelta(look, dx, dy);
    looked = true;
}

function onMouseMove(event) {
    if (!gate) return;

    // A mouse movement is the look only while the lock is held, or while the left button
    // is down as a drag — the fallback for a device with a mouse that will not lock.
    // Otherwise the mouse is the plain cursor, driving the lobby's hover and click rays
    // and nothing else. Neither path is a resting position, so a mouse sitting still
    // turns nothing.
    if (!document.pointerLockElement && !(event.buttons & 1)) return;

    moveLook(event.movementX, event.movementY);
}

function onTouchStart(event) {
    const touch = event.touches[0];
    if (!touch) return;

    lastTouch = { x: touch.clientX, y: touch.clientY };
}

function onTouchMove(event) {
    const touch = event.touches[0];
    if (!touch) return;

    if (!lastTouch) {
        onTouchStart(event);
        return;
    }

    // Deltas, then re-anchor: a finger has no resting position in the frame to be
    // measured from, so a drag reports its own movement the way a locked mouse does.
    moveLook(touch.clientX - lastTouch.x, touch.clientY - lastTouch.y);
    lastTouch = { x: touch.clientX, y: touch.clientY };

    // Registered non-passive for this: otherwise the page scrolls under the drag.
    if (event.cancelable) event.preventDefault();
}

function onTouchEnd() {
    lastTouch = null;
}

function setOnTarget(isOnTarget) {
    if (reticle) reticle.classList.toggle('reticle--on-target', isOnTarget);
}

/** Opens the lobby for walking and looking. The gate is what the walk is leashed to. */
export function enableLobbyWalk(gateObject) {
    gate = gateObject;
    held.clear();

    button = gateObject.getObjectByName(BUTTON_NAME);
    locksPointer = deviceLocksPointer();

    if (!listening) {
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('touchstart', onTouchStart, { passive: true });
        window.addEventListener('touchmove', onTouchMove, { passive: false });
        window.addEventListener('touchend', onTouchEnd);
        document.addEventListener('pointerlockchange', onPointerLockChange);
        document.addEventListener('pointerlockerror', onPointerLockError);
        listening = true;
    }

    // The look writes the camera's orientation outright every frame. OrbitControls
    // keeps its own idea of where the camera points and would snap it back on the next
    // drag, so it stands down for the lobby. closeDoors() hands control back and
    // re-aims the camera at the doors on the way, so nothing is lost by this.
    cameraControls.enabled = false;

    canvas = document.getElementById('canvasEl');
    reticle = document.getElementById('reticle');

    if (canvas) {
        canvas.addEventListener('click', onCanvasClick);
        canvas.classList.remove('lobby-look');
    }

    // The look starts from where the fly-by left the camera rather than from straight
    // ahead, so the first movement carries on from the arrival instead of snapping to
    // it. The zero movement is what holds the pitch inside the look's own limits, and
    // OrbitControls never rolls the camera, so there is no roll in the arrival for
    // taking a yaw and a pitch out of it to drop.
    const arrived = euler.setFromQuaternion(getActiveCamera().quaternion, 'YXZ');

    look = applyLookDelta({ yaw: arrived.y, pitch: arrived.x }, 0, 0);
    looked = false;
    lastTouch = null;

    setOnTarget(false);
    if (reticle) reticle.classList.add('reticle--visible');

    // Whatever the clock has been holding since the last time it was read is the loading
    // screen, not a step.
    clock.getDelta();
}

/** Called when the doors open — the opening move owns the camera from there. */
export function disableLobbyWalk() {
    held.clear();
    gate = null;
    button = null;
    lastTouch = null;
    locksPointer = false;

    if (canvas) {
        canvas.removeEventListener('click', onCanvasClick);
        canvas.classList.remove('lobby-look');
    }

    // OrbitControls takes the camera back after the doors, and it reads the pointer's
    // position to drag with — which a held lock freezes while it hides the cursor. Let
    // go of it here or the drag comes back broken.
    if (document.pointerLockElement) document.exitPointerLock();

    setOnTarget(false);
    if (reticle) reticle.classList.remove('reticle--visible');

    // cameraControls is left alone on purpose: openDoors has already switched it off
    // for the opening move, and closeDoors switches it back on.

    if (!listening) return;

    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
    document.removeEventListener('pointerlockchange', onPointerLockChange);
    document.removeEventListener('pointerlockerror', onPointerLockError);
    listening = false;
}

/**
 * Aims the camera where the look says it is looking. Nothing here reads a pointer
 * position or the clock: the orientation is written from the accumulated look, which
 * only a mouse movement changes — so the camera holds still until the mouse moves, and
 * the reticle at the centre of the frame marks exactly where the camera is pointing,
 * which is what makes it usable as an aim point.
 */
function updateLook() {
    if (!looked || !gate) return;

    const camera = getActiveCamera();

    // YXZ — yaw first, pitch inside it. Any other order lets pitch roll the horizon as
    // it climbs, which the eye reads as the world tipping over.
    camera.quaternion.setFromEuler(euler.set(look.pitch, look.yaw, 0, 'YXZ'));

    camera.getWorldDirection(facing);
    cameraControls.target.copy(camera.position).addScaledVector(facing, TARGET_OFFSET);

    updateReticle();
}

/**
 * Lights the reticle up while it is actually on the button, so the user knows the
 * click will land. One ray against the button alone — the scene now carries 2500
 * instanced asteroids and is not worth re-intersecting every frame for this.
 */
function updateReticle() {
    if (!button) return;

    raycastFromCentre([button], hit => setOnTarget(hit !== null));
}

/**
 * One frame of walking and looking. Called from Animate.js before the scene is drawn.
 */
export function updateLobbyWalk() {
    // Read the clock every frame, whether or not anything is being walked, or the first
    // key pressed after a pause spends the whole pause walking.
    const seconds = Math.min(clock.getDelta(), MAX_FRAME_SECONDS);

    if (!gate) return;

    // Looking comes first: the walk is steered by where the camera faces, so it has to
    // see this frame's facing rather than the last one's.
    updateLook();

    if (held.size === 0) return;

    const camera = getActiveCamera();

    camera.getWorldDirection(facing);

    const delta = walkDelta(held, facing, seconds);

    if (delta.x === 0 && delta.z === 0) return;

    camera.position.set(
        camera.position.x + delta.x,
        camera.position.y + delta.y,
        camera.position.z + delta.z,
    );

    const clamped = clampToGate(camera.position, gate.position);

    camera.position.set(clamped.x, clamped.y, clamped.z);

    // The target is placed from where the camera ended up rather than moved by the same
    // step, because the leash may have just stopped the camera dead: a target that
    // walked on regardless would end up somewhere else entirely, and the next drag would
    // swing the camera round that point instead of turning it in place.
    cameraControls.target.copy(camera.position).addScaledVector(facing, TARGET_OFFSET);
}
